import React, { FormEvent, useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { toAbsoluteUrl } from '../../../../../_metronic/helpers/AssetHelpers';

type SearchType = 'contains' | 'number';

type Inputs = {
  key: string;
  title: string;
  searchType?: SearchType;
  minWidth?: number;
  maxWidth?: number;
};

type SearchValues = {
  [key: string]:
    | { type: 'contains'; value: string }
    | { type: 'in'; values: { value: any }[] }
    | string
    | number
    | undefined;
};

type Props = {
  inputs: Inputs[];
  section: string;
  globalFilterValue: Record<string, any>;
  onSubmit: (searchValues: SearchValues) => void;
  onClear: (searchValues: SearchValues) => void;
  dependency?: string | undefined;
};

const SearchInputGroup = ({
  inputs,
  section,
  globalFilterValue,
  onSubmit,
  onClear,
  dependency,
}: Props) => {
  const [searchValues, setSearchValues] = useState<Record<string, string>>({});
  const intl = useIntl();

  useEffect(
    () => {
      if (globalFilterValue[section]) {
        const globalFilterSearchingKeys = Object.keys(globalFilterValue[section]).filter((key) =>
          inputs.map((input) => input.key).includes(key)
        );
        const globalFilterSearchingValues = globalFilterSearchingKeys.reduce(
          (accumulator, currentValue) => {
            const filterVal = globalFilterValue[section][currentValue];
            let extractedValue = '';

            if (filterVal !== undefined && filterVal !== null) {
              if (typeof filterVal === 'object' && 'value' in filterVal) {
                extractedValue = filterVal.value;
              } else {
                extractedValue = String(filterVal);
              }
            }

            return {
              ...accumulator,
              [currentValue]: extractedValue !== '' ? extractedValue : undefined,
            };
          },
          {}
        );
        setSearchValues(globalFilterSearchingValues);
      }
    },
    dependency ? [dependency] : []
  );

  const handleInputChange = (key: string, value: string, searchType?: SearchType) => {
    let finalValue = value;

    if (searchType === 'number') {
      finalValue = value.replace(/[^0-9.]/g, '');

      const dotCount = (finalValue.match(/\./g) || []).length;
      if (dotCount > 1) {
        return;
      }
    }

    setSearchValues({ ...searchValues, [key]: finalValue });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formattedSearchValue: SearchValues = {};

    const inputTypeMap = inputs.reduce((acc, input) => {
      acc[input.key] = input.searchType;
      return acc;
    }, {} as Record<string, SearchType>);

    for (let key in searchValues) {
      const value = searchValues[key];

      if (value === '' || value === undefined) {
        formattedSearchValue[key] = undefined;
      } else {
        const type = inputTypeMap[key];

        if (type === 'number') {
          formattedSearchValue[key] = { type: 'in', values: [{ value: Number(value) }] };
        } else {
          formattedSearchValue[key] = { type: 'contains', value: value };
        }
      }
    }
    onSubmit(formattedSearchValue);
  };

  const handleClear = (key: string) => {
    setSearchValues({ ...searchValues, [key]: '' });
    onClear({ [key]: undefined });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className='pb-2'>
        {/* Content */}
        <div className='menu-item d-inline-flex flex-wrap'>
          {inputs.map((input) => (
            <div className='pt-1 me-7 ' key={input.key}>
              <span className='text-dark text-uppercase fs-8 ls-1'>{input.title}</span>
              <div className='position-relative pt-1 pb-0 d-flex'>
                {/* Input */}
                <input
                  type={input.searchType === 'number' ? 'text' : 'text'}
                  className='form-control h-40px px-2 pe-7 fw-normal fs-7 text-gray-800'
                  name={input.key}
                  value={searchValues?.[input.key] ?? ''}
                  style={{
                    height: '40.42px',
                    minWidth: input?.minWidth ?? '280px',
                    maxWidth: input?.maxWidth ?? undefined,
                  }}
                  onChange={(e) => handleInputChange(input.key, e.target.value, input.searchType)}
                />
                {/* Clear */}
                {searchValues?.[input.key] !== '' && searchValues?.[input.key] !== undefined && (
                  <SVG
                    onClick={() => handleClear(input.key)}
                    className='position-absolute top-50 start-100 translate-middle aside-input ms-n4'
                    src={toAbsoluteUrl('/media/svg/general/cross.svg')}
                  />
                )}
              </div>
            </div>
          ))}

          {/* Search */}
          <div className='py-0'>
            <button
              className='btn btn-secondary px-2 py-1 mt-7 fs-6'
              style={{ height: '40.42px', minWidth: '140px' }}
              type='submit'
            >
              {intl.formatMessage({ id: 'FILTER.SEARCH' })}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SearchInputGroup;
